import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, ArrowRight, Calendar, DollarSign, Truck, Wrench } from 'lucide-react';

export const RentalPage = () => {
  const rentalBenefits = [
    "Sem necessidade de investimento inicial elevado",
    "Flexibilidade de contratos",
    "Manutenção preventiva incluída",
    "Substituição imediata em caso de falha",
    "Suporte técnico incluído",
    "Dinâmica mais moderna e atual do mercado"
  ];

  const rentalProcess = [
    {
      icon: <Calendar size={32} />,
      title: "1. Consulta",
      description: "Entre em contato e informe suas necessidades de equipamento e período desejado"
    },
    {
      icon: <DollarSign size={32} />,
      title: "2. Proposta",
      description: "Receba uma proposta personalizada com valores e condições de locação"
    },
    {
      icon: <Truck size={32} />,
      title: "3. Entrega",
      description: "Instalação profissional no local com treinamentos da equipe"
    },
    {
      icon: <Wrench size={32} />,
      title: "4. Suporte",
      description: "Manutenção contínua e suporte técnico durante todo o período"
    }
  ];

  const equipmentAvailable = [
    {
      name: "Ressonância Magnética",
      description: "RM de alto campo",
      href: "/ressonancia"
    },
    {
      name: "Tomógrafo",
      description: "CT Scanner de última geração",
      href: "/tomografia"
    },
    {
      name: "Ultrassom",
      description: "Sistemas portáteis e fixos",
      href: "/ultrassom"
    },
    {
      name: "Densitometria Óssea",
      description: "Sistema DEXA para análise óssea",
      href: "/densitometria"
    },
    {
      name: "Mamógrafo",
      description: "Equipamento completo para diagnóstico de mama",
      href: "/mamografia"
    },
    {
      name: "Raio-X",
      description: "Sistemas fixos e portáteis",
      href: "/raio-x"
    },
    {
      name: "CR (Radiografia Computadorizada)",
      description: "Sistema de digitalização de placas",
      href: "/cr"
    },
    {
      name: "DR (Radiografia Digital)",
      description: "Captura digital instantânea",
      href: "/dr"
    },
    {
      name: "Workstation (Estação de Trabalho)",
      description: "Estações para visualização de imagens",
      href: "/workstation"
    },
    {
      name: "PACS",
      description: "Sistema de arquivamento de imagens",
      href: "/pacs"
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-cyan-50 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900">
              Locação de Equipamentos Hospitalares
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Flexibilidade e tecnologia de ponta sem grandes investimentos. Alugue equipamentos médicos de alta qualidade com manutenção e suporte inclusos.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Vantagens da Locação
            </h2>
            <p className="text-xl text-gray-600">
              Por que escolher locação ao invés de compra?
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rentalBenefits.map((benefit, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <CheckCircle className="text-green-600" size={24} />
                  </div>
                  <p className="text-gray-700 font-medium">{benefit}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Como Funciona a Locação
            </h2>
            <p className="text-xl text-gray-600">
              Processo simples e rápido
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {rentalProcess.map((step, index) => (
              <div key={index} className="text-center space-y-4">
                <div className="mx-auto h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                  {step.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Available Equipment */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Equipamentos Disponíveis para Locação
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {equipmentAvailable.map((equipment, index) => (
              <Link key={index} to={equipment.href}>
                <Card className="hover:shadow-lg hover:border-blue-600 transition-all duration-300 h-full cursor-pointer">
                  <CardContent className="p-8 space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <h3 className="text-2xl font-bold text-gray-900">{equipment.name}</h3>
                        <p className="text-gray-600">{equipment.description}</p>
                        <p className="text-blue-600 font-medium text-sm">Ver detalhes →</p>
                      </div>
                      <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="text-blue-600" size={24} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-6">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Pronto para Alugar Equipamentos?
            </h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              A forma mais prática, moderna e vantajosa de ter acesso aos equipamentos que você precisa.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Solicitar Proposta
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
